import { useCallback, useState } from 'react';
import { useHeader } from '../../../../../../../../../../context';
import type { Pos, PosSearchResponse } from '../../../../../../../../../../interfaces';

/**
 * Connects an independent drawer search to the selector snapshot captured at opening.
 *
 * ---
 * 🧠 **Context**
 *
 * - Requires an open drawer with pharmacy.posSelector.props in the {@link useHeader} context.
 * - Uses the live search adapter but retains the snapshot selection/navigation controls; closes after successful asynchronous selection.
 *
 * ---
 * @example
 * ```ts
 * const drawer = usePosSelectorDrawer();
 * const selectedPharmacy = drawer.storedPosList.find((pos) => pos.pointOfSaleId === 'pdv-centro');
 * ```
 *
 * ---
 * @function usePosSelectorDrawer
 * @returns {ReturnType<typeof usePosSelectorDrawer>} Captured selector controls, independent search state and drawer actions.
 */
export function usePosSelectorDrawer() {
	const {
		pharmacy: { posSelector, setPosSelector, getUsePosSearch },
	} = useHeader();

	const {
		loading,
		isSimulated,
		selectedPos,
		storedPosList,
		handleSelectPos,
		hasMoreThanFourPos,
		handleSearch,
		handleSeeMore,
	} = posSelector.props!;

	const [posList, setPosList] = useState<Pos[] | undefined>(undefined);

	const handleSetPosList = useCallback((response?: PosSearchResponse) => {
		if (!response) return setPosList([]);

		const posResponse = isSimulated
			? response
			: (response as Exclude<PosSearchResponse, Pos[]>).content?.pointOfSaleList;

		return setPosList(posResponse as Pos[] | undefined);
	}, []);

	const {
		inputProps,
		search,
		loading: searchLoading,
	} = getUsePosSearch({
		onSuccess: handleSetPosList,
		onSearchClean() {
			setPosList(undefined);
		},
		onError() {
			setPosList([]);
		},
		paginationParams: { size: 4 },
	});

	const handleCloseDrawer = useCallback(() => {
		setPosSelector((prev) => ({ ...prev, expanded: false }));
	}, []);

	const isSinglePos = !!storedPosList && storedPosList.length === 1;

	const handleChangePos = async (posId: string) => {
		document.body.style.pointerEvents = 'auto';

		await handleSelectPos({ posId });

		handleCloseDrawer();
	};

	return {
		loading,
		isSimulated,
		selectedPos,
		storedPosList,
		hasMoreThanFourPos,
		handleSearch,
		handleSeeMore,
		posList,
		inputProps,
		search,
		searchLoading,
		handleCloseDrawer,
		isSinglePos,
		handleChangePos,
	};
}
